import React, { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CanvasSettingsTab } from "./canvas-settings-tab";
import { MetadataSettingsTab } from "./metadata-settings-tab";
import { LayerSettingsTab } from "./layer-settings-tab";
import { CanvasSize, CanvasSettings } from "@/types/diagram";

// Default settings
const DEFAULT_SETTINGS: CanvasSettings = {
  canvas: {
    canvasSize: { width: 800, height: 600 },
    showAttachmentPoints: false,
  },
  metadataLabel: {
    minSpacing: 20,
    minYSpacing: 30,
    smoothingAngle: Math.PI / 4,
    stepSize: 10,
  },
  layerLabel: {
    width: 200,
    lineSpacing: 1.2,
    fontFamily: "Arial",
    fontSize: 14,
    fontWeight: "normal",
    hideLabels: false,
  },
};

interface AdvancedCanvasSettingsProps {
  onSaveSettings: (settings: CanvasSettings) => void;
  initialSettings?: Partial<CanvasSettings>;
}

const STORAGE_KEY = "canvasSettings";

export const AdvancedCanvasSettings: React.FC<AdvancedCanvasSettingsProps> = ({
  onSaveSettings,
  initialSettings = {},
}) => {
  const [settings, setSettings] = useState<CanvasSettings>(() => {
    const savedSettings = localStorage.getItem(STORAGE_KEY);
    if (savedSettings) {
      return JSON.parse(savedSettings);
    }
    return { ...DEFAULT_SETTINGS, ...initialSettings };
  });
  const [hasChanges, setHasChanges] = useState(false);

  const updateSettings = (
    category: keyof CanvasSettings,
    key: string,
    value: number | string | boolean | CanvasSize
  ) => {
    const newSettings = (function () {
      if (category === "canvas") {
        return {
          ...settings,
          canvas: {
            ...settings.canvas,
            [key]: value,
          },
        };
      } else if (category === "metadataLabel") {
        return {
          ...settings,
          metadataLabel: {
            ...settings.metadataLabel,
            [key]: value,
          },
        };
      } else if (category === "layerLabel") {
        return {
          ...settings,
          layerLabel: {
            ...settings.layerLabel,
            [key]: value,
          },
        };
      }
      return settings;
    })();
    
    setSettings(newSettings);
    setHasChanges(true);
  };

  const handleSave = () => {
    onSaveSettings(settings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    setHasChanges(false);
  };

  useEffect(() => {
  }, [settings]);

  return (
    <div className="space-y-4">
      <Tabs defaultValue="canvas" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-surface-card">
          <TabsTrigger
            value="canvas"
            className="text-white data-[state=active]:bg-primary data-[state=active]:text-white"
          >
            Canvas
          </TabsTrigger>
          <TabsTrigger
            value="metadata"
            className="text-white data-[state=active]:bg-primary data-[state=active]:text-white"
          >
            Metadata Labels
          </TabsTrigger>
          <TabsTrigger
            value="layer"
            className="text-white data-[state=active]:bg-primary data-[state=active]:text-white"
          >
            Layer Labels
          </TabsTrigger>
        </TabsList>

        <TabsContent value="canvas" className="mt-4">
          <div className="p-4 bg-surface-card rounded-lg">
            <CanvasSettingsTab
              canvas={settings.canvas}
              onChange={(key, value) => updateSettings("canvas", key, value)}
            />
          </div>
        </TabsContent>

        <TabsContent value="metadata" className="mt-4">
          <div className="p-4 bg-surface-card rounded-lg">
            <MetadataSettingsTab
              settings={settings.metadataLabel}
              onChange={(key, value) => updateSettings("metadataLabel", key, value)}
            />
          </div>
        </TabsContent>

        <TabsContent value="layer" className="mt-4">
          <div className="p-4 bg-surface-card rounded-lg">
            <LayerSettingsTab
              settings={settings.layerLabel}
              onChange={(key, value) => updateSettings("layerLabel", key, value)}
            />
          </div>
        </TabsContent>
      </Tabs>

      {hasChanges && (
        <div className="flex justify-end mt-4">
          <Button
            onClick={handleSave}
            className="bg-primary hover:bg-primary-dark text-white"
          >
            Save Settings
          </Button>
        </div>
      )}
    </div>
  );
};